/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";

import { getColorPalette } from "@/helpers/chartHelper";

import {
  Cell,
  Label,
  Legend,
  LegendProps,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { twMerge } from "tailwind-merge";

const RADIAN = Math.PI / 180;

// Custom label for outside
const renderCustomizedLabel = (
  label: any,
  totalValue: number,
  chartWidth: number
) => {
  const { cx, cy, midAngle, outerRadius, fill, value, name } = label;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  // Leader line start (outer edge of pie)
  const sx = cx + outerRadius * cos;
  const sy = cy + (outerRadius + 10) * sin;
  // Leader line bend
  const mx = cx + (outerRadius + 10) * cos;
  const my = cy + (outerRadius + 50) * sin;

  // Leader line end at left or right edge (with padding)
  const isRight = cos >= 0;
  const padding = 18;
  const labelWidth = 80;
  const ex = isRight ? chartWidth - padding : padding;
  const ey = my;

  const exText = isRight ? chartWidth - padding - labelWidth : padding;
  // Hide label for small slices (<5%) to avoid overlap
  if (value / totalValue < 0.05) return null;

  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <foreignObject x={exText} y={ey - 20} width={80} height={20}>
        <div
          className={twMerge(
            "truncate text-xs",
            isRight ? "text-right" : "text-left"
          )}
        >
          {name}
        </div>
      </foreignObject>
      <foreignObject x={exText} y={ey + 5} width={80} height={20}>
        <div
          className={twMerge(
            "truncate text-xs font-semibold",
            isRight ? "text-right" : "text-left"
          )}
          style={{ color: fill }}
        >
          {value?.toLocaleString()}
        </div>
      </foreignObject>
    </g>
  );
};

// Center label with total
const renderCenterLabel = (content: any, data: any[]) => {
  const { viewBox } = content;
  const total = data.reduce((acc, cur) => acc + cur.value, 0);
  const totalStr = total.toLocaleString();
  let fontSize = 24;
  if (totalStr.length > 6) fontSize = 18;
  if (totalStr.length > 9) fontSize = 14;
  if (totalStr.length > 12) fontSize = 10;
  return (
    <text
      x={viewBox.cx}
      y={viewBox.cy}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={fontSize}
      fontWeight={700}
      fill="#000000E5"
    >
      {totalStr}
    </text>
  );
};

// Custom tooltip formatting
const CustomTooltip = ({ active, payload, totalValue }: any) => {
  if (active && payload?.length) {
    const { name, value } = payload[0];
    const percentValue = (value / totalValue) * 100;
    return (
      <div
        className={twMerge(
          "flex flex-col gap-2 bg-white border border-whisper rounded-[10px] p-3",
          "shadow-[0_2px_8px_rgba(0,0,0,0.08)] min-w-[170px]"
        )}
      >
        <span className="text-xs text-txtHeading font-normal">{name}</span>
        <span className="text-xs text-txtHeading font-semibold">
          {value.toLocaleString()} ({percentValue.toFixed(1)}%)
        </span>
      </div>
    );
  }
  return null;
};

const ScrollableLegend = (props: LegendProps): JSX.Element | null => {
  const { payload } = props;

  if (!payload) return null;

  return (
    <div
      className={twMerge(
        "legends max-h-32 overflow-y-auto flex flex-wrap gap-y-1 gap-x-2 flex-center custom-scrollbar",
        "w-full"
      )}
    >
      {payload.map((entry, index) => (
        <div
          key={`legend-item-${entry.value}-${index}`}
          className="flex items-center text-xs text-black text-opacity-70 whitespace-nowrap"
        >
          <span
            className="inline-block w-2 h-2 rounded-full mr-1"
            style={{
              background: entry.color,
              border: "1px solid #eee",
              flexShrink: 0
            }}
          />
          <span
            className="inline-block truncate max-w-[60px]"
            title={String(entry.value)}
          >
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export type DonutChartItem = { name: string; value: any };

type DonutChartProps = {
  data?: DonutChartItem[];
  height?: string | number;
};

/**
 * DonutChart component that dynamically generates enough colors for all slices.
 */
const DonutChart = ({ data = [], height }: DonutChartProps) => {
  const totalValue = data.reduce((acc, cur) => acc + cur.value, 0);
  // Generate as many colors as needed for this data set
  const COLORS = getColorPalette(data.length);
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(430);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    // Use ResizeObserver for accurate width updates
    const observer = new window.ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        for (const entry of entries)
          setChartWidth(entry.contentRect.width || 430);
      }
    );
    observer.observe(node);
    // Call once for initial mount
    setChartWidth(node.offsetWidth || 430);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!data?.length || totalValue === 0)
    return (
      <div className="flex-center h-[400px] px-4">
        <p className="text-base text-txtHeading font-semibold text-center">
          No data available to display. Please check your data or try adjusting
          the configuration menu.
        </p>
      </div>
    );

  return (
    <div ref={containerRef} style={{ width: "100%", height: height || 600 }}>
      <ResponsiveContainer width="100%" height={height || 600}>
        <PieChart className="flex-center">
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            startAngle={90}
            endAngle={-270}
            innerRadius={50}
            outerRadius={100}
            paddingAngle={0}
            dataKey="value"
            labelLine={false}
            label={(label: any) =>
              renderCustomizedLabel(label, totalValue, chartWidth)
            }
            isAnimationActive={false}
          >
            <Label
              content={(content) => renderCenterLabel(content, data)}
              position="center"
            />
            {data.map((entry, idx) => (
              <Cell
                className="outline-none"
                key={`cell-${entry.name}`}
                fill={COLORS[idx % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={(content) => (
              <CustomTooltip {...content} totalValue={totalValue} />
            )}
          />
          <Legend
            content={ScrollableLegend as any}
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DonutChart;
